import { AsyncPipe } from '@angular/common';
import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { DomSanitizer, SafeStyle } from '@angular/platform-browser';

const IMG_VALIDATE_TIMEOUT_MS = 5000;
const IMG_CACHE_DURATION = 30 * 60 * 1000;
const FALLBACK_URL = './images/kc-logo-square.png';

const logoCache = new Map<string, { url: string; timestamp: number }>();
const pendingProbes = new Map<string, Promise<string>>();

function validateImageUrl(url: string): Promise<string> {
  return new Promise<string>((resolve) => {
    const trimmed = url.trim();
    if (!trimmed) return resolve('');
    if (typeof Image === 'undefined') return resolve(trimmed);
    if (!/^https?:\/\//i.test(trimmed) && !trimmed.startsWith('./'))
      return resolve('');

    const img = new Image();
    let settled = false;
    const finish = (result: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeoutId);
      img.onload = null;
      img.onerror = null;
      img.src = '';
      resolve(result);
    };
    const timeoutId = setTimeout(() => finish(''), IMG_VALIDATE_TIMEOUT_MS);
    img.onload = () => finish(trimmed);
    img.onerror = () => finish('');
    img.src = trimmed;
  });
}

function probeLogoUrl(url: string): Promise<string> {
  if (!url) return Promise.resolve(FALLBACK_URL);

  const cached = logoCache.get(url);
  if (cached && Date.now() - cached.timestamp < IMG_CACHE_DURATION) {
    return Promise.resolve(cached.url);
  }

  const pending = pendingProbes.get(url);
  if (pending) return pending;

  const probe = validateImageUrl(url).then((result) => {
    const finalUrl = result || FALLBACK_URL;
    logoCache.set(url, { url: finalUrl, timestamp: Date.now() });
    pendingProbes.delete(url);
    return finalUrl;
  });
  pendingProbes.set(url, probe);
  return probe;
}

export type TokenLogoSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-token-logo',
  standalone: true,
  imports: [AsyncPipe],
  templateUrl: './token-logo.component.html',
  styleUrl: './token-logo.component.scss',
  host: {
    '[class.is-loading]': 'isLoading()',
    '[class.size-sm]': 'size() === "sm"',
    '[class.size-md]': 'size() === "md"',
    '[class.size-lg]': 'size() === "lg"',
    '[attr.aria-label]': 'label() ? label() + " logo" : null',
    role: 'img',
  },
})
export class TokenLogoComponent {
  private readonly sanitizer = inject(DomSanitizer);

  logoUrl = input<string | undefined>(undefined);
  label = input<string>('');
  size = input<TokenLogoSize>('md');

  isLoading = signal(true);

  readonly backgroundStyle = computed(async (): Promise<SafeStyle> => {
    const url = this.logoUrl()?.trim() ?? '';
    const resolved = await probeLogoUrl(url);
    return this.sanitizer.bypassSecurityTrustStyle(
      `url("${encodeURI(resolved)}")`,
    );
  });

  private _probeGeneration = 0;

  constructor() {
    effect(async () => {
      const gen = ++this._probeGeneration;
      const url = this.logoUrl()?.trim() ?? '';
      const cached = url ? logoCache.get(url) : undefined;
      if (!cached || Date.now() - cached.timestamp >= IMG_CACHE_DURATION) {
        this.isLoading.set(true);
      }
      await this.backgroundStyle();
      if (gen === this._probeGeneration) {
        this.isLoading.set(false);
      }
    });
  }
}
